import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Text.css';
import { getSentencesByUnit, getUnitIdFromStorage } from '../../services/sentenceService';
import Alert from '../../components/Alert';
import SettingsModal from '../../components/SettingsModal';
import { articleReadingConfig } from '../../config/settingsConfig';
import { formatSentenceText } from '../../utils/formatText';

const Text = () => {
  const navigate = useNavigate();
  const [sentencesData, setSentencesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unitId, setUnitId] = useState(null);
  
  const isRequestInProgressRef = useRef(false);
  const loadSentencesCalledRef = useRef(false);
  
  const [selectedArticles, setSelectedArticles] = useState([]); // 存储选中的文章/对话ID
  const [selectedArticleItems, setSelectedArticleItems] = useState([]); // 存储右侧显示的文章/对话
  const [deletingArticles, setDeletingArticles] = useState([]); // 存储正在删除动画中的文章/对话
  const [isInverseMode, setIsInverseMode] = useState(false); // 反选模式是否激活
  const [isClearing, setIsClearing] = useState(false); // 是否正在执行清空操作
  const [isInitialized, setIsInitialized] = useState(false); // 是否已经初始化加载
  const [isContentReady, setIsContentReady] = useState(false); // 内容是否准备好显示
  const [expandedArticles, setExpandedArticles] = useState({}); // 存储每个文章/对话的展开状态
  
  // 音频播放相关状态
  const [currentAudio, setCurrentAudio] = useState(null); // 当前播放的音频对象
  const [playingSentenceId, setPlayingSentenceId] = useState(null); // 当前播放的句子ID
  const [playingArticleId, setPlayingArticleId] = useState(null); // 当前播放的文章ID
  const audioRef = useRef(null); // 音频对象引用
  const isPlayingRef = useRef(false); // 是否正在播放

  // 课文领读相关状态
  const [showAlert, setShowAlert] = useState(false); // 提示框显示状态
  const [alertMessage, setAlertMessage] = useState('请先选择文章或对话'); // 提示框消息
  const [showArticleReadingSettings, setShowArticleReadingSettings] = useState(false); // 课文领读设置弹窗显示状态
  const [selectedLessonId, setSelectedLessonId] = useState(null); // 当前选中的课时ID

  // 从localStorage加载已选文章/对话
  const loadSelectedArticlesFromStorage = (currentUnitId) => {
    try {
      const saved = localStorage.getItem(`selectedArticles_${currentUnitId}`);
      const result = saved ? JSON.parse(saved) : [];
      return result;
    } catch (error) {
      console.error('加载已选文章/对话失败:', error);
      return [];
    }
  };

  // 保存已选文章/对话到localStorage
  const saveSelectedArticlesToStorage = (articles, currentUnitId) => {
    try {
      localStorage.setItem(`selectedArticles_${currentUnitId}`, JSON.stringify(articles));
    } catch (error) {
      console.error('保存已选文章/对话失败:', error);
    }
  };

  // 加载句子数据的函数
  const loadSentences = async (forceReload = false) => {
    try {
      if (isRequestInProgressRef.current) {
        return;
      }

      const savedUnitId = getUnitIdFromStorage();
      if (!savedUnitId) {
        setError('未找到选择的单元，请先选择学习单元');
        setIsContentReady(true);
        return;
      }
      
      if (!forceReload && unitId === savedUnitId && sentencesData && isInitialized) {
        setIsContentReady(true);
        return;
      }

      isRequestInProgressRef.current = true;
      setLoading(true);
      setError(null);
      setUnitId(savedUnitId);
      
      const startTime = Date.now();
      const minLoadingTime = 300;
      
      // 获取单元句子数据
      const data = await getSentencesByUnit(savedUnitId);
      
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minLoadingTime) {
        await new Promise(resolve => setTimeout(resolve, minLoadingTime - elapsedTime));
      }
      
      setSentencesData(data);
      setIsInitialized(true);
      
      // 🔧 如果有课时数据，默认选中第一个课时
      // forceReload 时强制重新选择第一个课时（切换单元时）
      if (data && data.lessons && data.lessons.length > 0) {
        if (forceReload || !selectedLessonId) {
          setSelectedLessonId(data.lessons[0].lesson_id);
        }
      }
      
      // 从localStorage恢复选中状态
      const savedArticleIds = loadSelectedArticlesFromStorage(savedUnitId);
      if (savedArticleIds.length > 0) {
        // 根据保存的ID查找对应的文章对象
        const restoredArticles = [];
        data.lessons.forEach(lesson => {
          lesson.articles.forEach(article => {
            if (savedArticleIds.includes(article.article_id)) {
              restoredArticles.push(article);
            }
          });
        });
        setSelectedArticles(savedArticleIds);
        setSelectedArticleItems(restoredArticles);
      }
      
      setTimeout(() => {
        setIsContentReady(true);
      }, 50);
      
    } catch (err) {
      console.error('加载句子数据失败:', err);
      setError(err.message || '加载句子数据失败，请稍后重试');
      setIsContentReady(true);
    } finally {
      setLoading(false);
      isRequestInProgressRef.current = false;
    }
  };

  // 监听单元变化事件，统一处理数据加载
  useEffect(() => {
    let timeoutId;
    let hasReceivedEvent = false;

    const handleUnitChange = (event) => {
      const { unitId: newUnitId } = event.detail;
      hasReceivedEvent = true;
      
      // 清除延迟检查，因为已经接收到事件
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      // 使用 ref 检查请求状态，避免闭包问题
      if (newUnitId && newUnitId !== unitId && !isRequestInProgressRef.current) {
        // 重新加载时要强制刷新，因为单元变了
        // 清空当前选中状态
        setSelectedArticles([]);
        setSelectedArticleItems([]);
        setIsInitialized(false);
        setIsContentReady(false);
        // 传入 forceReload=true，会在加载完成后自动选中第一个课时
        loadSentences(true);
      } else if (newUnitId === unitId && sentencesData) {
        // 如果单元ID相同且已有数据，直接标记内容准备好
        setIsContentReady(true);
      }
    };

    // 添加自定义事件监听器
    window.addEventListener('unitChanged', handleUnitChange);

    // 延迟检查策略，只在没有接收到事件时执行一次
    timeoutId = setTimeout(() => {
      // 使用 ref 检查是否已调用过 loadSentences
      if (!hasReceivedEvent && !loadSentencesCalledRef.current && !isRequestInProgressRef.current) {
        loadSentencesCalledRef.current = true; // 标记已调用
        loadSentences();
      } else {
        // 如果已有数据但内容未准备好，标记为准备好
        if (sentencesData && !isContentReady) {
          setIsContentReady(true);
        }
      }
    }, 100);

    // 清理函数
    return () => {
      window.removeEventListener('unitChanged', handleUnitChange);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [unitId, sentencesData, isContentReady]);

  // 组件卸载时清理音频
  useEffect(() => {
    return () => {
      stopCurrentAudio();
    };
  }, []);

  // 监听选中文章变化，自动保存到localStorage
  // 只有在数据初始化完成后才保存，避免刷新时用空数组覆盖已保存的数据
  useEffect(() => {
    if (unitId && isInitialized && selectedArticles.length >= 0) {
      saveSelectedArticlesToStorage(selectedArticles, unitId);
    }
  }, [selectedArticles, unitId, isInitialized]);

  // 处理文章/对话点击
  const handleArticleClick = (article) => {
    const isSelected = selectedArticles.includes(article.article_id);
    
    // 手动选择文章时，取消反选模式
    setIsInverseMode(false);
    
    if (isSelected) {
      // 取消选中
      setSelectedArticles(prev => prev.filter(id => id !== article.article_id));
      setSelectedArticleItems(prev => prev.filter(item => item.article_id !== article.article_id));
    } else {
      // 选中
      setSelectedArticles(prev => [...prev, article.article_id]);
      setSelectedArticleItems(prev => [...prev, article]);
    }
  };

  // 处理课时全选
  const handleLessonSelectAll = (lesson) => {
    const allArticleIds = lesson.articles.map(article => article.article_id);
    const isAllSelected = allArticleIds.every(id => selectedArticles.includes(id));
    
    if (isAllSelected) {
      // 取消全选
      setSelectedArticles(prev => prev.filter(id => !allArticleIds.includes(id)));
      setSelectedArticleItems(prev => prev.filter(item => !allArticleIds.includes(item.article_id)));
    } else {
      // 全选
      const newSelectedIds = [...new Set([...selectedArticles, ...allArticleIds])];
      const newSelectedItems = [...selectedArticleItems];
      
      lesson.articles.forEach(article => {
        if (!newSelectedItems.find(i => i.article_id === article.article_id)) {
          newSelectedItems.push(article);
        }
      });
      
      setSelectedArticles(newSelectedIds);
      setSelectedArticleItems(newSelectedItems);
    }
  };

  // 判断课时是否全选
  const isLessonAllSelected = (lesson) => {
    const allArticleIds = lesson.articles.map(article => article.article_id);
    return allArticleIds.length > 0 && allArticleIds.every(id => selectedArticles.includes(id));
  };

  // 全选
  const handleSelectAll = () => {
    if (!sentencesData || !sentencesData.lessons) return;
    
    const allArticles = [];
    sentencesData.lessons.forEach(lesson => {
      lesson.articles.forEach(article => {
        allArticles.push(article);
      });
    });
    
    const allArticleIds = allArticles.map(article => article.article_id);
    
    // 取消反选模式
    setIsInverseMode(false);
    
    // 检查是否已全选，如果是则取消全选
    const isAllSelected = allArticleIds.length > 0 && 
      allArticleIds.every(id => selectedArticles.includes(id));
    
    if (isAllSelected) {
      // 取消全选
      setSelectedArticles([]);
      setSelectedArticleItems([]);
    } else {
      // 全选
      setSelectedArticles(allArticleIds);
      setSelectedArticleItems(allArticles);
    }
  };

  // 反选
  const handleSelectInverse = () => {
    if (!sentencesData || !sentencesData.lessons) return;
    
    const allArticles = [];
    sentencesData.lessons.forEach(lesson => {
      lesson.articles.forEach(article => {
        allArticles.push(article);
      });
    });
    
    if (isInverseMode) {
      // 如果已经是反选模式，则取消并清空
      setIsInverseMode(false);
      setSelectedArticles([]);
      setSelectedArticleItems([]);
    } else {
      // 执行反选并激活反选模式
      const newSelectedIds = allArticles.filter(article => !selectedArticles.includes(article.article_id)).map(article => article.article_id);
      const newSelectedItems = allArticles.filter(article => !selectedArticles.includes(article.article_id));
      
      setSelectedArticles(newSelectedIds);
      setSelectedArticleItems(newSelectedItems);
      setIsInverseMode(true);
    }
  };

  // 一键选中/取消所有对话（classify = 1）
  const handleSelectDialogues = () => {
    if (!sentencesData || !sentencesData.lessons) return;
    
    // 取消反选模式
    setIsInverseMode(false);
    
    const dialogueArticles = [];
    sentencesData.lessons.forEach(lesson => {
      lesson.articles.forEach(article => {
        if (article.classify === 1) {
          dialogueArticles.push(article);
        }
      });
    });
    
    const dialogueIds = dialogueArticles.map(article => article.article_id);
    
    // 检查是否所有对话都已选中
    const allDialoguesSelected = dialogueIds.length > 0 && 
      dialogueIds.every(id => selectedArticles.includes(id));
    
    if (allDialoguesSelected) {
      // 取消所有对话的选中
      const newSelectedIds = selectedArticles.filter(id => !dialogueIds.includes(id));
      const newSelectedItems = selectedArticleItems.filter(item => item.classify !== 1);
      setSelectedArticles(newSelectedIds);
      setSelectedArticleItems(newSelectedItems);
    } else {
      // 选中所有对话
      const newSelectedIds = [...new Set([...selectedArticles, ...dialogueIds])];
      const newSelectedItems = [...selectedArticleItems];
      
      dialogueArticles.forEach(article => {
        if (!newSelectedItems.find(i => i.article_id === article.article_id)) {
          newSelectedItems.push(article);
        }
      });
      
      setSelectedArticles(newSelectedIds);
      setSelectedArticleItems(newSelectedItems);
    }
  };

  // 一键选中/取消所有课文（classify = 2）
  const handleSelectArticles = () => {
    if (!sentencesData || !sentencesData.lessons) return;
    
    // 取消反选模式
    setIsInverseMode(false);
    
    const textArticles = [];
    sentencesData.lessons.forEach(lesson => {
      lesson.articles.forEach(article => {
        if (article.classify === 2) {
          textArticles.push(article);
        }
      });
    });
    
    const textArticleIds = textArticles.map(article => article.article_id);
    
    // 检查是否所有课文都已选中
    const allArticlesSelected = textArticleIds.length > 0 && 
      textArticleIds.every(id => selectedArticles.includes(id));
    
    if (allArticlesSelected) {
      // 取消所有课文的选中
      const newSelectedIds = selectedArticles.filter(id => !textArticleIds.includes(id));
      const newSelectedItems = selectedArticleItems.filter(item => item.classify !== 2);
      setSelectedArticles(newSelectedIds);
      setSelectedArticleItems(newSelectedItems);
    } else {
      // 选中所有课文
      const newSelectedIds = [...new Set([...selectedArticles, ...textArticleIds])];
      const newSelectedItems = [...selectedArticleItems];
      
      textArticles.forEach(article => {
        if (!newSelectedItems.find(i => i.article_id === article.article_id)) {
          newSelectedItems.push(article);
        }
      });
      
      setSelectedArticles(newSelectedIds);
      setSelectedArticleItems(newSelectedItems);
    }
  };

  // 判断是否全选
  const isAllArticlesSelected = () => {
    if (!sentencesData || !sentencesData.lessons) return false;
    
    const allArticleIds = [];
    sentencesData.lessons.forEach(lesson => {
      lesson.articles.forEach(article => {
        allArticleIds.push(article.article_id);
      });
    });
    
    return allArticleIds.length > 0 && allArticleIds.every(id => selectedArticles.includes(id));
  };

  // 判断是否所有对话都已选中
  const isAllDialoguesSelected = () => {
    if (!sentencesData || !sentencesData.lessons) return false;
    
    const dialogueIds = [];
    sentencesData.lessons.forEach(lesson => {
      lesson.articles.forEach(article => {
        if (article.classify === 1) {
          dialogueIds.push(article.article_id);
        }
      });
    });
    
    return dialogueIds.length > 0 && dialogueIds.every(id => selectedArticles.includes(id));
  };

  // 判断是否所有课文都已选中
  const isAllTextsSelected = () => {
    if (!sentencesData || !sentencesData.lessons) return false;
    
    const textIds = [];
    sentencesData.lessons.forEach(lesson => {
      lesson.articles.forEach(article => {
        if (article.classify === 2) {
          textIds.push(article.article_id);
        }
      });
    });
    
    return textIds.length > 0 && textIds.every(id => selectedArticles.includes(id));
  };

  // 处理第三部分课文项的点击（删除功能）
  const handleArticleItemClick = (articleId) => {
    // 如果已经在删除动画中或正在清空，则忽略点击
    if (deletingArticles.includes(articleId) || isClearing) {
      return;
    }
    
    // 开始删除动画
    setDeletingArticles(prev => [...prev, articleId]);
    
    // 延迟执行删除操作，让动画完成
    setTimeout(() => {
      // 从selectedArticles中移除这篇课文
      setSelectedArticles(prev => prev.filter(id => id !== articleId));
      // 从selectedArticleItems中移除
      setSelectedArticleItems(prev => prev.filter(item => item.article_id !== articleId));
      // 清除删除动画状态
      setDeletingArticles(prev => prev.filter(id => id !== articleId));
    }, 600); // 动画持续时间600ms，与CSS动画协调
  };

  // 清空选中
  const handleClearSelected = async () => {
    if (selectedArticleItems.length === 0) return;
    
    setIsClearing(true);
    setDeletingArticles(selectedArticleItems.map(item => item.article_id));
    
    await new Promise(resolve => setTimeout(resolve, 600));
    
    setSelectedArticles([]);
    setSelectedArticleItems([]);
    setDeletingArticles([]);
    setIsClearing(false);
  };

  // 切换文章展开/收起状态
  const toggleArticleExpand = (articleId, e) => {
    e.stopPropagation(); // 阻止事件冒泡，避免触发文章选择
    setExpandedArticles(prev => {
      const currentState = prev[articleId] === true; // 默认收起（undefined 或 false）
      return {
        ...prev,
        [articleId]: !currentState
      };
    });
  };

  // 停止当前音频播放
  const stopCurrentAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setCurrentAudio(null);
    setPlayingSentenceId(null);
    setPlayingArticleId(null);
    isPlayingRef.current = false;
  };

  // 播放单个句子音频
  const playSentenceAudio = async (sentence) => {
    try {
      // 如果正在播放同一个句子，则停止播放
      if (playingSentenceId === sentence.id) {
        stopCurrentAudio();
        return;
      }

      // 停止当前播放
      stopCurrentAudio();

      // 检查音频资源
      if (!sentence.audio || !sentence.audio.res) {
        console.warn('句子没有音频资源:', sentence.id);
        return;
      }

      // 创建新的音频对象
      const audio = new Audio(sentence.audio.res);
      audioRef.current = audio;
      setCurrentAudio(audio);
      setPlayingSentenceId(sentence.id);
      isPlayingRef.current = true;

      // 播放结束后清理状态
      audio.onended = () => {
        setPlayingSentenceId(null);
        isPlayingRef.current = false;
        audioRef.current = null;
        setCurrentAudio(null);
      };

      // 播放出错时清理状态
      audio.onerror = (error) => {
        console.error('音频播放失败:', error);
        stopCurrentAudio();
      };

      // 开始播放
      await audio.play();
    } catch (error) {
      console.error('播放句子音频失败:', error);
      stopCurrentAudio();
    }
  };

  // 播放文章所有句子音频（连续播放）
  const playArticleAudio = async (article) => {
    try {
      // 如果正在播放同一篇文章，则停止播放
      if (playingArticleId === article.article_id) {
        stopCurrentAudio();
        return;
      }

      // 停止当前播放
      stopCurrentAudio();

      // 过滤出有音频的句子
      const sentencesWithAudio = article.sentences.filter(
        sentence => sentence.audio && sentence.audio.res
      );

      if (sentencesWithAudio.length === 0) {
        console.warn('文章没有可播放的音频');
        return;
      }

      // 先设置播放状态
      const articleId = article.article_id;
      setPlayingArticleId(articleId);
      isPlayingRef.current = true;

      // 依次播放每个句子
      for (let i = 0; i < sentencesWithAudio.length; i++) {
        const sentence = sentencesWithAudio[i];
        
        // 检查是否被中断
        if (!isPlayingRef.current) {
          break;
        }

        // 创建音频对象
        const audio = new Audio(sentence.audio.res);
        audioRef.current = audio;
        setCurrentAudio(audio);
        setPlayingSentenceId(sentence.id);

        // 等待当前句子播放完成
        await new Promise((resolve, reject) => {
          audio.onended = resolve;
          audio.onerror = reject;
          audio.play().catch(reject);
        });

        // 句子之间的间隔（300ms）
        if (i < sentencesWithAudio.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // 全部播放完成后清理状态
      stopCurrentAudio();
    } catch (error) {
      console.error('播放文章音频失败:', error);
      stopCurrentAudio();
    }
  };

  // 课文领读按钮处理函数
  const handleArticleReading = () => {
    // 检查是否有选中的文章/对话
    if (selectedArticleItems.length === 0) {
      // 显示提示框
      setAlertMessage('请先选择文章或对话');
      setShowAlert(true);
      return;
    }
    
    // 显示课文领读设置弹窗
    setShowArticleReadingSettings(true);
  };

  // 处理课文领读设置弹窗关闭
  const handleArticleReadingSettingsClose = () => {
    setShowArticleReadingSettings(false);
  };

  // 处理开始课文领读
  const handleArticleReadingStart = (settings) => {
    // 关闭弹窗
    setShowArticleReadingSettings(false);
    
    // 准备课文领读数据
    const textReadingData = {
      articles: selectedArticleItems,
      settings: settings
    };
    
    // 将数据存储到 sessionStorage
    sessionStorage.setItem('textReadingData', JSON.stringify(textReadingData));
    
    // 跳转到课文领读页面
    navigate('/text-reading');
  };

  // 角色扮演按钮处理函数
  const handleRolePlay = () => {
    // 检查是否有选中的文章/对话
    if (selectedArticleItems.length === 0) {
      // 显示提示框
      setAlertMessage('请先选择文章或对话');
      setShowAlert(true);
      return;
    }
    
    // 筛选出对话类型（classify = 1）
    const dialogues = selectedArticleItems.filter(item => item.classify === 1);
    
    // 检查是否至少选择了一篇对话
    if (dialogues.length === 0) {
      // 显示提示框，提示需要选择对话
      setAlertMessage('请至少选择一篇对话');
      setShowAlert(true);
      return;
    }
    
    // 准备角色扮演数据
    const rolePlayData = {
      selectedDialogues: dialogues
    };
    
    // 将数据存储到 sessionStorage
    sessionStorage.setItem('rolePlayData', JSON.stringify(rolePlayData));
    
    // 跳转到角色扮演页面
    navigate('/role-play');
  };

  // 渲染对话类型（classify = 1）
  const renderDialogue = (article) => {
    const isSelected = selectedArticles.includes(article.article_id);
    const isExpanded = expandedArticles[article.article_id] === true; // 默认收起
    
    // 播放对话句子音频
    const handlePlaySentence = (sentence, e) => {
      e.stopPropagation();
      playSentenceAudio(sentence);
    };
    
    return (
      <div className="dialogue-container">
        <div className="article-header">
          <span className="article-type-tag dialogue-tag">对话</span>
          <h4 className="article-title">{article.title}</h4>
          <button 
            className={`article-toggle ${isExpanded ? 'expanded' : 'collapsed'}`}
            onClick={(e) => toggleArticleExpand(article.article_id, e)}
            title={isExpanded ? '收起' : '展开'}
          >
            <span className="toggle-icon">▼</span>
          </button>
        </div>
        {isExpanded && (
          <div className="dialogue-content">
            {article.sentences.map((sentence) => {
              return (
                <div 
                  key={sentence.id}
                  className="dialogue-item"
                >
                  <div className="dialogue-item-box">
                    <div className="dialogue-avatar">
                      {sentence.role_image && sentence.role_image.res ? (
                        <img src={sentence.role_image.res} alt={sentence.role_image.title || '角色'} />
                      ) : (
                        <div className="avatar-placeholder">👤</div>
                      )}
                    </div>
                    <div className="dialogue-bubble-wrapper">
                      <div className={`dialogue-bubble ${playingSentenceId === sentence.id ? 'playing' : ''}`}>
                        <div className="dialogue-english">{formatSentenceText(sentence.title)}</div>
                        <button 
                          className={`dialogue-play-button ${playingSentenceId === sentence.id ? 'playing' : ''}`}
                          onClick={(e) => handlePlaySentence(sentence, e)}
                          title={playingSentenceId === sentence.id ? '停止播放' : '播放句子'}
                        >
                          <svg 
                            viewBox="0 0 24 24" 
                            className="speaker-icon"
                            fill="none"
                          >
                            {/* 喇叭主体 */}
                            <path 
                              d="M11 5L6 9H2v6h4l5 4V5z" 
                              fill="currentColor"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            {/* 第一道声波 */}
                            <path 
                              d="M15.54 8.46a5 5 0 0 1 0 7.07" 
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                            {/* 第二道声波 */}
                            <path 
                              d="M18.36 5.64a9 9 0 0 1 0 12.73" 
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // 渲染文章类型（classify = 2）
  const renderArticle = (article) => {
    const isSelected = selectedArticles.includes(article.article_id);
    const isExpanded = expandedArticles[article.article_id] === true; // 默认收起
    
    // 播放文章音频
    const handlePlayArticle = (e) => {
      e.stopPropagation();
      playArticleAudio(article);
    };
    
    return (
      <div className="article-container">
        <div className="article-header">
          <span className="article-type-tag article-tag">语篇</span>
          <h4 className="article-title">{article.title}</h4>
          <button 
            className={`article-toggle ${isExpanded ? 'expanded' : 'collapsed'}`}
            onClick={(e) => toggleArticleExpand(article.article_id, e)}
            title={isExpanded ? '收起' : '展开'}
          >
            <span className="toggle-icon">▼</span>
          </button>
        </div>
        {isExpanded && (
          <div className="article-content-wrapper">
            <div className="article-content">
              <div className="article-text">
                {article.sentences.map((sentence, index) => {
                  return (
                    <span 
                      key={sentence.id}
                      className="article-sentence"
                    >
                      {formatSentenceText(sentence.title)}
                      {index < article.sentences.length - 1 ? ' ' : ''}
                    </span>
                  );
                })}
              </div>
              <button 
                className={`article-play-button ${playingArticleId === article.article_id ? 'playing' : ''}`}
                onClick={handlePlayArticle}
                title={playingArticleId === article.article_id ? '停止播放' : '播放文章'}
              >
                <svg 
                  viewBox="0 0 24 24" 
                  className="speaker-icon"
                  fill="none"
                >
                  {/* 喇叭主体 */}
                  <path 
                    d="M11 5L6 9H2v6h4l5 4V5z" 
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  {/* 第一道声波 */}
                  <path 
                    d="M15.54 8.46a5 5 0 0 1 0 7.07" 
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  {/* 第二道声波 */}
                  <path 
                    d="M18.36 5.64a9 9 0 0 1 0 12.73" 
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染课时标签
  const renderLessonTabs = () => {
    if (!sentencesData || !sentencesData.lessons || sentencesData.lessons.length === 0) {
      return null;
    }

    return (
      <div className="lesson-tabs">
        {sentencesData.lessons.map((lesson) => (
          <div
            key={lesson.lesson_id}
            className={`lesson-tab ${selectedLessonId === lesson.lesson_id ? 'lesson-tab-active' : ''}`}
            onClick={() => setSelectedLessonId(lesson.lesson_id)}
          >
            {lesson.lesson_title}
          </div>
        ))}
      </div>
    );
  };

  // 渲染句子列表
  const renderSentencesList = () => {
    if (!isContentReady) {
      return (
        <div className="sentences-placeholder">
          <div className="placeholder-content">
            <div className="placeholder-icon">📖</div>
            <p>准备加载课文内容...</p>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="sentences-loading">
          <div className="loading-spinner"></div>
          <p>正在加载课文...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="sentences-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>重新加载</button>
        </div>
      );
    }

    if (!sentencesData || !sentencesData.lessons || sentencesData.lessons.length === 0) {
      return (
        <div className="sentences-empty">
          <p>该单元暂无课文数据</p>
        </div>
      );
    }

    // 找到当前选中的课时
    const currentLesson = sentencesData.lessons.find(lesson => lesson.lesson_id === selectedLessonId);
    
    if (!currentLesson) {
      return (
        <div className="sentences-empty">
          <p>请选择一个课时</p>
        </div>
      );
    }

    const isAllSelected = isLessonAllSelected(currentLesson);

    return (
      <div className={`sentences-content ${isContentReady ? 'sentences-content-ready' : ''}`}>
        <div className="lessons-container">
          <div className="lesson-section">
            <div className="lesson-header">
              <div 
                className={`lesson-select-all ${isAllSelected ? 'lesson-select-all-checked' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLessonSelectAll(currentLesson);
                }}
                title={isAllSelected ? '取消全选' : '全选本课时句子'}
              >
                {isAllSelected && <span className="select-all-check">✓</span>}
              </div>
              <h3 className="lesson-title">{currentLesson.lesson_title}</h3>
            </div>
            
            <div className="articles-container">
              {currentLesson.articles.map((article) => {
                const isSelected = selectedArticles.includes(article.article_id);
                const isExpanded = expandedArticles[article.article_id] === true;
                return (
                  <div 
                    key={article.article_id} 
                    className={`article-wrapper ${isSelected ? 'article-selected' : ''} ${!isExpanded ? 'collapsed' : ''}`}
                    onClick={() => handleArticleClick(article)}
                  >
                    {article.classify === 1 ? renderDialogue(article) : renderArticle(article)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染右侧选中文章/对话列表
  const renderSelectedSentencesList = () => {
    return (
      <div className="selected-sentences-container">
        <div className="selected-sentences-header">
          <h3>已选课文 ({selectedArticleItems.length})</h3>
        </div>
        
        {selectedArticleItems.length === 0 ? (
          <div className="selected-sentences-empty">
            <p>暂无选中的课文</p>
          </div>
        ) : (
          <>
            <div className="selected-sentences-list">
              {selectedArticleItems.map((article) => {
                const isDeleting = deletingArticles.includes(article.article_id);
                return (
                  <div 
                    key={article.article_id}
                    className={`selected-sentence-item ${isDeleting ? 'selected-sentence-item-deleting' : ''}`}
                    onClick={() => handleArticleItemClick(article.article_id)}
                  >
                    <div className="selected-sentence-content">
                      <div className="selected-sentence-english">{article.title}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button 
              className="clear-selected-button"
              onClick={handleClearSelected}
              disabled={isClearing}
            >
              {isClearing ? '清空中...' : '清空已选'}
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="text-container">
      {/* 左侧区域 */}
      <div className="text-left">
        {/* 第一部分 - 主要内容区 */}
        <div className="text-section-1">
          {renderLessonTabs()}
          <div className="text-section-1-scroll">
            {renderSentencesList()}
          </div>
          <div className="text-section-1-bottom">
            <div className="selection-controls">
              <label className="selection-control">
                <input 
                  type="checkbox" 
                  onChange={handleSelectAll}
                  checked={isAllArticlesSelected()}
                />
                <span>全选</span>
              </label>
              
              <label className="selection-control">
                <input 
                  type="checkbox" 
                  onChange={handleSelectInverse}
                  checked={isInverseMode}
                />
                <span>反选</span>
              </label>
              
              <label className="selection-control">
                <input 
                  type="checkbox" 
                  checked={isAllDialoguesSelected()}
                  onChange={handleSelectDialogues}
                />
                <span>对话</span>
              </label>
              
              <label className="selection-control">
                <input 
                  type="checkbox" 
                  checked={isAllTextsSelected()}
                  onChange={handleSelectArticles}
                />
                <span>语篇</span>
              </label>
            </div>
          </div>
        </div>
        
        {/* 第二部分 - 底部控制区 */}
        <div className="text-section-2">
          <div className="section2-buttons-container">
            <button 
              className="section2-button section2-button-left"
              onClick={handleArticleReading}
            >
              课文领读
            </button>
            <button 
              className="section2-button section2-button-right"
              onClick={handleRolePlay}
            >
              角色扮演
            </button>
          </div>
        </div>
      </div>
      
      {/* 右侧区域 */}
      <div className="text-right">
        {renderSelectedSentencesList()}
      </div>
      
      {/* 提示框 */}
      <Alert
        show={showAlert}
        message={alertMessage}
        onHide={() => setShowAlert(false)}
        type="warning"
        duration={1200}
      />
      
      {/* 课文领读设置弹窗 */}
      <SettingsModal
        isOpen={showArticleReadingSettings}
        onClose={handleArticleReadingSettingsClose}
        onStart={handleArticleReadingStart}
        title={articleReadingConfig.title}
        settingItems={articleReadingConfig.settingItems}
        defaultSettings={articleReadingConfig.defaultSettings}
      />
    </div>
  );
};

export default Text;

